import React, { useEffect, useState } from 'react';

const Calculator = () => {
  const [mode, setMode] = useState('add');
  const [amount, setAmount] = useState(100);
  const [vatRate, setVatRate] = useState(20);
  const [currency, setCurrency] = useState('$');
  const [selectedCountry, setSelectedCountry] = useState('custom');
  const [results, setResults] = useState({
    originalAmount: 100,
    vatAmount: 20,
    totalAmount: 120
  });
  const [history, setHistory] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('vat-history');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    }
    return [];
  });
  const [copied, setCopied] = useState(false);

  const currencies = [
    { value: '$', label: 'USD - US Dollar' },
    { value: '€', label: 'EUR - Euro' },
    { value: '£', label: 'GBP - British Pound' },
    { value: '¥', label: 'JPY - Japanese Yen' },
    { value: 'CN¥', label: 'CNY - Chinese Yuan' },
    { value: '₹', label: 'INR - Indian Rupee' },
    { value: '₣', label: 'CHF - Swiss Franc' },
    { value: 'A$', label: 'AUD - Australian Dollar' },
    { value: 'C$', label: 'CAD - Canadian Dollar' },
    { value: 'R$', label: 'BRL - Brazilian Real' },
    { value: '₽', label: 'RUB - Russian Ruble' },
    { value: '₩', label: 'KRW - South Korean Won' },
    { value: '₪', label: 'ILS - Israeli Shekel' },
    { value: 'R', label: 'ZAR - South African Rand' },
    { value: '₦', label: 'NGN - Nigerian Naira' },
    { value: '₨', label: 'PKR - Pakistani Rupee' },
    { value: '₱', label: 'PHP - Philippine Peso' },
    { value: '฿', label: 'THB - Thai Baht' },
    { value: '₫', label: 'VND - Vietnamese Dong' },
    { value: '₴', label: 'UAH - Ukrainian Hryvnia' },
    { value: '₸', label: 'KZT - Kazakhstani Tenge' },
    { value: 'د.إ', label: 'AED - UAE Dirham' },
    { value: 'SAR', label: 'SAR - Saudi Riyal' },
    { value: 'NZ$', label: 'NZD - New Zealand Dollar' },
    { value: 'S$', label: 'SGD - Singapore Dollar' },
    { value: 'HK$', label: 'HKD - Hong Kong Dollar' },
    { value: 'MX$', label: 'MXN - Mexican Peso' },
    { value: 'ARS', label: 'ARS - Argentine Peso' },
    { value: 'CLP', label: 'CLP - Chilean Peso' },
    { value: 'COP', label: 'COP - Colombian Peso' },
    { value: 'RM', label: 'MYR - Malaysian Ringgit' },
    { value: 'Rp', label: 'IDR - Indonesian Rupiah' },
    { value: 'TRY', label: 'TRY - Turkish Lira' },
    { value: 'EGP', label: 'EGP - Egyptian Pound' },
    { value: 'KES', label: 'KES - Kenyan Shilling' },
    { value: 'PEN', label: 'PEN - Peruvian Sol' },
    { value: 'BDT', label: 'BDT - Bangladeshi Taka' }
  ];

  const vatPresets = [5, 10, 15, 20];

  const countries = [
    { code: 'custom', name: 'Custom / Other', rate: null, currency: null },
    { code: 'uk', name: 'United Kingdom (20%)', rate: 20, currency: '£' },
    { code: 'de', name: 'Germany (19%)', rate: 19, currency: '€' },
    { code: 'fr', name: 'France (20%)', rate: 20, currency: '€' },
    { code: 'it', name: 'Italy (22%)', rate: 22, currency: '€' },
    { code: 'es', name: 'Spain (21%)', rate: 21, currency: '€' },
    { code: 'nl', name: 'Netherlands (21%)', rate: 21, currency: '€' },
    { code: 'be', name: 'Belgium (21%)', rate: 21, currency: '€' },
    { code: 'at', name: 'Austria (20%)', rate: 20, currency: '€' },
    { code: 'pl', name: 'Poland (23%)', rate: 23, currency: '€' },
    { code: 'se', name: 'Sweden (25%)', rate: 25, currency: '€' },
    { code: 'dk', name: 'Denmark (25%)', rate: 25, currency: '€' },
    { code: 'ie', name: 'Ireland (23%)', rate: 23, currency: '€' },
    { code: 'pt', name: 'Portugal (23%)', rate: 23, currency: '€' },
    { code: 'fi', name: 'Finland (24%)', rate: 24, currency: '€' },
    { code: 'gr', name: 'Greece (24%)', rate: 24, currency: '€' },
    { code: 'cz', name: 'Czech Republic (21%)', rate: 21, currency: '€' },
    { code: 'ro', name: 'Romania (19%)', rate: 19, currency: '€' },
    { code: 'hu', name: 'Hungary (27%)', rate: 27, currency: '€' },
    { code: 'bg', name: 'Bulgaria (20%)', rate: 20, currency: '€' },
    { code: 'ch', name: 'Switzerland (7.7%)', rate: 7.7, currency: '₣' },
    { code: 'no', name: 'Norway (25%)', rate: 25, currency: '€' },
    { code: 'us', name: 'United States (0% VAT)', rate: 0, currency: '$' },
    { code: 'ca', name: 'Canada GST (5-15%)', rate: 13, currency: 'C$' },
    { code: 'au', name: 'Australia GST (10%)', rate: 10, currency: 'A$' },
    { code: 'nz', name: 'New Zealand GST (15%)', rate: 15, currency: 'NZ$' },
    { code: 'in', name: 'India GST (18%)', rate: 18, currency: '₹' },
    { code: 'cn', name: 'China VAT (13%)', rate: 13, currency: 'CN¥' },
    { code: 'jp', name: 'Japan Consumption Tax (10%)', rate: 10, currency: '¥' },
    { code: 'kr', name: 'South Korea VAT (10%)', rate: 10, currency: '₩' },
    { code: 'sg', name: 'Singapore GST (9%)', rate: 9, currency: 'S$' },
    { code: 'my', name: 'Malaysia SST (10%)', rate: 10, currency: 'RM' },
    { code: 'th', name: 'Thailand VAT (7%)', rate: 7, currency: '฿' },
    { code: 'ph', name: 'Philippines VAT (12%)', rate: 12, currency: '₱' },
    { code: 'id', name: 'Indonesia VAT (11%)', rate: 11, currency: 'Rp' },
    { code: 'vn', name: 'Vietnam VAT (10%)', rate: 10, currency: '₫' },
    { code: 'ae', name: 'UAE VAT (5%)', rate: 5, currency: 'د.إ' },
    { code: 'sa', name: 'Saudi Arabia VAT (15%)', rate: 15, currency: 'SAR' },
    { code: 'il', name: 'Israel VAT (17%)', rate: 17, currency: '₪' },
    { code: 'za', name: 'South Africa VAT (15%)', rate: 15, currency: 'R' },
    { code: 'ng', name: 'Nigeria VAT (7.5%)', rate: 7.5, currency: '₦' },
    { code: 'eg', name: 'Egypt VAT (14%)', rate: 14, currency: 'EGP' },
    { code: 'ke', name: 'Kenya VAT (16%)', rate: 16, currency: 'KES' },
    { code: 'br', name: 'Brazil ICMS (varies)', rate: 18, currency: 'R$' },
    { code: 'mx', name: 'Mexico VAT (16%)', rate: 16, currency: 'MX$' },
    { code: 'ar', name: 'Argentina VAT (21%)', rate: 21, currency: 'ARS' },
    { code: 'cl', name: 'Chile VAT (19%)', rate: 19, currency: 'CLP' },
    { code: 'co', name: 'Colombia VAT (19%)', rate: 19, currency: 'COP' },
    { code: 'pe', name: 'Peru VAT (18%)', rate: 18, currency: 'PEN' },
    { code: 'ru', name: 'Russia VAT (20%)', rate: 20, currency: '₽' },
    { code: 'ua', name: 'Ukraine VAT (20%)', rate: 20, currency: '₴' },
    { code: 'tr', name: 'Turkey VAT (20%)', rate: 20, currency: 'TRY' },
    { code: 'pk', name: 'Pakistan GST (17%)', rate: 17, currency: '₨' },
    { code: 'bd', name: 'Bangladesh VAT (15%)', rate: 15, currency: 'BDT' },
    { code: 'hk', name: 'Hong Kong (0% VAT)', rate: 0, currency: 'HK$' }
  ];

  const formatCurrency = (value) => {
    // Handle edge cases: NaN, Infinity, or invalid numbers
    if (!isFinite(value) || isNaN(value)) {
      return currency + '0.00';
    }
    // Ensure value is not negative for display
    const safeValue = Math.max(0, value);
    return currency + safeValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const computeResults = (amountValue, vatRateValue, currentMode) => {
    // Validate inputs - ensure they are valid numbers
    const safeAmount = isFinite(amountValue) && !isNaN(amountValue) ? Math.max(0, amountValue) : 0;
    const safeVatRate = isFinite(vatRateValue) && !isNaN(vatRateValue) ? Math.max(0, Math.min(100, vatRateValue)) : 0;

    let originalAmount;
    let vatAmount;
    let totalAmount;

    if (currentMode === 'add') {
      originalAmount = safeAmount;
      vatAmount = (safeAmount * safeVatRate) / 100;
      totalAmount = safeAmount + vatAmount;
    } else {
      totalAmount = safeAmount;
      if (safeVatRate === 0) {
        originalAmount = safeAmount;
        vatAmount = 0;
      } else {
        originalAmount = safeAmount / (1 + safeVatRate / 100);
        vatAmount = safeAmount - originalAmount;
      }
    }

    // Round to 2 decimal places to avoid floating point precision issues
    return {
      originalAmount: Math.round(originalAmount * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100
    };
  };

  const calculate = () => {
    const amountValue = parseFloat(amount) || 0;
    const vatRateValue = parseFloat(vatRate) || 0;

    const nextResults = computeResults(amountValue, vatRateValue, mode);
    setResults(nextResults);
    return { amountValue, vatRateValue, nextResults };
  };

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, amount, vatRate]);

  const handleVatPreset = (rate) => {
    setVatRate(rate);
  };

  const handleCountryChange = (e) => {
    const code = e.target.value;
    setSelectedCountry(code);
    const country = countries.find((c) => c.code === code);
    if (!country) return;
    if (country.rate !== null && country.rate !== undefined) {
      setVatRate(country.rate);
    }
    if (country.currency) {
      setCurrency(country.currency);
    }
  };

  const handleCalculateClick = () => {
    const { amountValue, vatRateValue, nextResults } = calculate();
    const entry = {
      id: Date.now(),
      mode,
      amount: amountValue,
      vatRate: vatRateValue,
      currency,
      originalAmount: nextResults.originalAmount,
      vatAmount: nextResults.vatAmount,
      totalAmount: nextResults.totalAmount
    };

    setHistory((prev) => {
      const updated = [entry, ...prev].slice(0, 10);
      try {
        localStorage.setItem('vat-history', JSON.stringify(updated));
      } catch {
        // ignore write errors
      }
      return updated;
    });
  };

  const handleClearHistory = () => {
    setHistory([]);
    try {
      localStorage.removeItem('vat-history');
    } catch {
      // ignore
    }
  };

  const handleCopyResults = async () => {
    const text = [
      `Mode: ${mode === 'add' ? 'Add VAT' : 'Remove VAT'}`,
      `Net Amount: ${formatCurrency(results.originalAmount)}`,
      `VAT Rate: ${parseFloat(vatRate) || 0}%`,
      `VAT Amount: ${formatCurrency(results.vatAmount)}`,
      `Gross Amount: ${formatCurrency(results.totalAmount)}`
    ].join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="calculator-section" id="calculator">
      <div className="calculator-container">
        <div className="calculator-left">
          <div className="calc-tabs">
            <button
              className={`tab-btn ${mode === 'add' ? 'active' : ''}`}
              onClick={() => setMode('add')}
            >
              Add VAT
            </button>
            <button
              className={`tab-btn ${mode === 'remove' ? 'active' : ''}`}
              onClick={() => setMode('remove')}
            >
              Remove VAT
            </button>
          </div>

          <div className="input-group">
            <label htmlFor="amount-input">Amount</label>
            <div className="input-wrapper">
              <input
                id="amount-input"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                min="0"
                step="0.01"
              />
              <span className="currency-symbol">{currency}</span>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="vat-rate-input">VAT Rate (%)</label>
            <div className="input-wrapper">
              <input
                id="vat-rate-input"
                type="number"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                placeholder="Enter VAT rate"
                min="0"
                max="100"
                step="0.1"
              />
              <span className="currency-symbol">%</span>
            </div>
            <div className="vat-presets">
              {vatPresets.map((preset) => (
                <div
                  key={preset}
                  className="vat-preset"
                  onClick={() => handleVatPreset(preset)}
                >
                  {preset}%
                </div>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="country-select">Country / Region</label>
            <div className="input-wrapper">
              <select
                id="country-select"
                value={selectedCountry}
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="currency-select">Currency</label>
            <div className="input-wrapper">
              <select
                id="currency-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {currencies.map((curr) => (
                  <option key={curr.value} value={curr.value}>
                    {curr.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button className="calc-btn" onClick={handleCalculateClick}>
            Calculate VAT
          </button>
        </div>

        <div className="calculator-right">
          <h2 style={{ marginBottom: '2rem', fontSize: '2rem' }}>Results</h2>
          <div className="results">
            <div className="result-item">
              <span className="result-label">Net Amount:</span>
              <span className="result-value">
                {formatCurrency(results.originalAmount)}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">VAT Amount:</span>
              <span className="result-value">
                {formatCurrency(results.vatAmount)}
              </span>
            </div>
            <div className="result-item">
              <span className="result-label">Gross Amount:</span>
              <span className="result-value">
                {formatCurrency(results.totalAmount)}
              </span>
            </div>
          </div>

          <div className="results-actions">
            <button
              type="button"
              className="copy-btn"
              onClick={handleCopyResults}
            >
              {copied ? 'Copied!' : 'Copy Results'}
            </button>
          </div>

          {history.length > 0 && (
            <div className="history">
              <div className="history-header">
                <h3>Recent calculations</h3>
                <button
                  type="button"
                  className="history-clear-btn"
                  onClick={handleClearHistory}
                >
                  Clear
                </button>
              </div>
              <ul className="history-list">
                {history.map((item) => (
                  <li key={item.id} className="history-item">
                    <div className="history-main">
                      <span className="history-amount">
                        {item.currency}
                        {item.amount.toFixed(2)}
                      </span>
                      <span className="history-mode">
                        {item.mode === 'add' ? 'Add VAT' : 'Remove VAT'}
                      </span>
                    </div>
                    <div className="history-meta">
                      <span>{item.vatRate}%</span>
                      <span>{formatCurrency(item.totalAmount)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Calculator;
